import './index.less';

import { Button, EToastType, toast, ToastContainer } from '@bfe/moly';
import { useState } from 'react';

import ResultModal from '@/components/ResultModal';
import SecurityComponent from '@/components/SecurityComponent';
import useTgBind from '@/hooks/useTgBind';
import { useUserProfile } from '@/hooks/useUserProfile';
import { useI18nContext } from '@/i18n/I18n';
import { bindApp, unbindApp } from '@/services/bindApp';
import { SCENES } from '@/utils/securityComponent';
import { sensors } from '@/utils/skynet';
import { bindAppFail, unbindAppFail } from '@/utils/trace-list/bindApp';
import { tgBindNotBotName } from '@/utils/trace-list/tgBind';

const TgBindBtn = ({
  status,
  onBindSuccess,
  onUnbindSuccess,
}: {
  status: boolean;
  onBindSuccess: (
    username: string,
    first_name: string,
    last_name: string,
  ) => void;
  onUnbindSuccess: () => void;
}) => {
  const [$t] = useI18nContext();
  const [showSecurityComponent, setShowSecurityComponent] = useState(false);
  const [telegramUserInfo, setTelegramUserInfo] = useState<any>(null);
  const { updateUserProfile } = useUserProfile();
  const [errorMsg, setErrorMsg] = useState('');
  const [errorModalTitle, setErrorModalTitle] = useState(
    status ? $t('failedToUnlinkTelegram') : $t('failedToLinkTelegram'),
  );

  const { botName } = useTgBind({
    tgCallback(params) {
      setTelegramUserInfo(params);
      setShowSecurityComponent(true);
      sensors?.click?.({
        page_name: 'AccountSecurityClick',
        button_name: 'settings_bn',
        model_name: 'show_linkaccount_model',
      });
    },
  });

  // 绑定 App
  const postBindApp = async (risk_token: string) => {
    try {
      await bindApp({ risk_token, telegram_callback: telegramUserInfo });
      toast({
        iconType: EToastType.SUCCESS,
        title: $t('tradingTeamBinded'),
      });
      sensors?.click?.({
        page_name: 'AccountSecurityClick',
        button_name: 'settings_success',
        model_name: 'show_linkaccount_model',
      });
      onBindSuccess?.(
        telegramUserInfo?.username,
        telegramUserInfo?.first_name,
        telegramUserInfo?.last_name,
      );
      updateUserProfile();
    } catch (error: any) {
      const msg = error?.ret_msg;
      const code = error?.ret_code;
      const errorMsg = $t(`${code}`, { ns: 'inverse-error-code' });
      const reason = `${code}: ${
        errorMsg !== `${code}` && code !== 40420 ? errorMsg : msg
      }`;
      // toast({
      //   iconType: EToastType.FAIL,
      //   title: reason,
      // });
      setErrorModalTitle($t('failedToLinkTelegram'));
      setErrorMsg(reason);
      sensors?.click?.({
        page_name: 'AccountSecurityClick',
        button_name: 'settings_fail',
        model_name: 'show_linkaccount_model',
      });
      // 绑定失败上报
      bindAppFail({
        risk_token,
        telegramUserInfo,
        error,
      });
    }
  };

  // 解绑 App
  const postUnbindApp = async (risk_token: string) => {
    try {
      await unbindApp({ risk_token });
      toast({
        iconType: EToastType.SUCCESS,
        title: $t('SuccessfullyUnlinked'),
      });
      sensors?.click?.({
        page_name: 'AccountSecurityClick',
        button_name: 'unlink_success',
        model_name: 'show_linkaccount_model',
      });
      onUnbindSuccess?.();
      updateUserProfile();
    } catch (error: any) {
      const msg = error?.ret_msg;
      const code = error?.ret_code;
      const errorMsg =
        code === 10016
          ? $t('systemError')
          : $t(`${code}`, { ns: 'inverse-error-code' });
      const reason = `${code}: ${errorMsg !== `${code}` ? errorMsg : msg}`;
      // toast({
      //   iconType: EToastType.FAIL,
      //   title: reason,
      // });
      setErrorModalTitle($t('failedToUnlinkTelegram'));
      setErrorMsg(reason);
      sensors?.click?.({
        page_name: 'AccountSecurityClick',
        button_name: 'unlink_fail',
        model_name: 'show_linkaccount_model',
      });
      // 解绑app失败上报
      unbindAppFail({
        risk_token,
        error,
      });
    }
  };

  // 安全组件验证成功回调
  const onSecuritySuccess = (risk_token: string) => {
    if (status) {
      // 解绑流程
      postUnbindApp(risk_token);
    } else {
      // 绑定流程
      postBindApp(risk_token);
    }
  };

  const onUnbind = () => {
    setShowSecurityComponent(true);
    sensors?.click?.({
      page_name: 'AccountSecurityClick',
      button_name: 'unlink_bn',
      model_name: 'show_linkaccount_model',
    });
  };

  if (!botName) {
    tgBindNotBotName();
    return null;
  }

  return (
    <>
      <div className="bind-tg-btn-wrap">
        {status ? (
          <Button
            size="small"
            data-radius="small"
            variant="secondary"
            onClick={onUnbind}
          >
            {$t('unlink')}
          </Button>
        ) : (
          <>
            <Button size="small" data-radius="small" variant="primary">
              {$t('Setting')}
            </Button>
          </>
        )}
        <div id="widget-container" style={status ? { zIndex: -1 } : {}}></div>
      </div>

      {/* 安全组件 */}
      {showSecurityComponent && (
        <SecurityComponent
          scene={
            status
              ? SCENES.UNBIND_THIRD_APP_TELEGRAM
              : SCENES.BIND_THIRD_APP_TELEGRAM
          }
          onSuccess={(risk_token) => {
            setShowSecurityComponent(false);
            onSecuritySuccess(risk_token);
          }}
          onClose={(riskTokenType, riskToken) => {
            // 如果安全组件下发了引导项，并且用户关闭了引导弹窗，也认为是校验成功
            if (riskTokenType === 'guide' && riskToken) {
              onSecuritySuccess(riskToken);
            }
            setShowSecurityComponent(false);
          }}
        />
      )}
      <ToastContainer />
      <ResultModal
        modalProps={{
          open: errorMsg !== '',
          size: 'medium',
          cancelText: false,
          okText: $t('Confirm'),
          onOk: () => {
            setErrorMsg('');
          },
          onClose: () => {
            setErrorMsg('');
          },
        }}
        resultProps={{
          title: errorModalTitle,
          subTitle: errorMsg,
          status: 'error',
        }}
      />
    </>
  );
};

export default TgBindBtn;
